import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { PARTNER_TYPES, PARTNER_STATUS, PARTNER_TYPE_LIST, commissionRuleText } from "@/lib/partnerConstants";
import { FieldSelect } from "@/components/ui/field-select";
import { PartnerDialog } from "@/components/partners/PartnerDialogs";
import { Handshake, Plus, Search, Loader2 } from "lucide-react";

export default function Partenaires() {
  const { orgId, can } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["partners", orgId, type, status, q],
    queryFn: async () => (await api.get("/api/partners", { params: {
      organization_id: orgId, type: type || undefined, status: status || undefined, q: q || undefined } })).data,
    enabled: !!orgId,
  });
  const partners = data?.partners || [];
  const k = data?.kpis || {};
  const currency = data?.currency || "MAD";

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="partenaires-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Partenaires</h1>
        {can("partners.manage") && <button onClick={() => setDlgOpen(true)} data-testid="new-partner-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouveau partenaire</button>}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Partenaires actifs" value={k.active} testid="partner-kpi-active" />
        <Kpi label="Total partenaires" value={k.total} testid="partner-kpi-total" tone="text-slate-900" />
        <Kpi label="Commissions à régler" value={formatMoney(k.commissions_due_cents, currency)} testid="partner-kpi-due" tone="text-indigo-700" />
        <Kpi label="Commissions projetées" value={formatMoney(k.commissions_pending_cents, currency)} testid="partner-kpi-pending" tone="text-amber-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="partner-search" className="w-56 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <FieldSelect testId="partner-filter-type" className="w-44" value={type || "ALL"} onChange={(v) => setType(v === "ALL" ? "" : v)} options={[{ value: "ALL", label: "Tout type" }, ...PARTNER_TYPE_LIST.map((r) => ({ value: r, label: PARTNER_TYPES[r] }))]} />
        <FieldSelect testId="partner-filter-status" className="w-40" value={status || "ALL"} onChange={(v) => setStatus(v === "ALL" ? "" : v)} options={[{ value: "ALL", label: "Tout statut" }, { value: "ACTIVE", label: "Actif" }, { value: "INACTIVE", label: "Inactif" }]} />
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : partners.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="partners-empty"><Handshake className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-3 text-base font-semibold text-slate-700">Aucun partenaire pour le moment</p><p className="mt-1 text-sm text-slate-400">Ajoutez hôtels, agences ou apporteurs d'affaires pour suivre leurs commissions.</p>{can("partners.manage") && <button onClick={() => setDlgOpen(true)} data-testid="partners-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouveau partenaire</button>}</div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white lg:block">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr>{["Référence", "Nom", "Type", "Commission", "Réservations", "À régler", "Statut"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {partners.map((p) => (
                  <tr key={p.id} onClick={() => nav(`/partenaires/${p.id}`)} data-testid={`partner-row-${p.id}`} className="cursor-pointer hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-medium text-slate-700">{p.partner_reference}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.name}</td>
                    <td className="px-4 py-3 text-slate-500">{PARTNER_TYPES[p.partner_type]}</td>
                    <td className="px-4 py-3 text-slate-500">{commissionRuleText(p.default_commission_type, p.default_commission_value, currency)}</td>
                    <td className="px-4 py-3 text-slate-500">{p.bookings_count}</td>
                    <td className="px-4 py-3 font-mono text-indigo-700">{formatMoney(p.commission_due_cents, currency)}</td>
                    <td className="px-4 py-3"><Badge s={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:hidden">
            {partners.map((p) => (
              <button key={p.id} onClick={() => nav(`/partenaires/${p.id}`)} data-testid={`partner-card-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-3 text-left">
                <div className="flex items-center justify-between"><p className="font-medium text-slate-800">{p.name}</p><Badge s={p.status} /></div>
                <p className="mt-0.5 text-xs text-slate-500">{p.partner_reference} · {PARTNER_TYPES[p.partner_type]} · {commissionRuleText(p.default_commission_type, p.default_commission_value, currency)}</p>
                <p className="mt-1 text-xs text-slate-400">{p.bookings_count} réservations · à régler <span className="font-mono text-indigo-700">{formatMoney(p.commission_due_cents, currency)}</span></p>
              </button>
            ))}
          </div>
        </>
      )}

      <PartnerDialog open={dlgOpen} onOpenChange={setDlgOpen} />
    </div>
  );
}

const Badge = ({ s }) => { const c = PARTNER_STATUS[s] || PARTNER_STATUS.ACTIVE; return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.cls}`} data-testid={`partner-status-${s}`}>{c.label}</span>; };
const Kpi = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}><p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p><p className={`mt-1.5 text-2xl font-bold ${tone}`}>{value ?? 0}</p></div>
);
